/**
 * A read-through cache for what the app shows, so a scorer with no signal still sees their
 * matches, scorecards and stats instead of an error.
 *
 * ## Why this lives in the app and not in the service worker
 *
 * ADR-0016's allowlist says the worker never touches `/api`, and that is not a limitation to
 * work around: a worker that cached authenticated responses could serve one account's data to
 * the next person to pick up a shared phone, which is the worst defect available here. So the
 * cache is app code, keyed by the signed-in account, and every rule below is about that.
 *
 * ## The rules
 *
 * - **Live first, always.** A read tries the network; the cache is what is shown when the
 *   network cannot answer. It never makes a fresh read slower or older.
 * - **Only "no answer" falls back.** No signal or a 5xx is served from the cache. A 401 or 403
 *   is a *refusal* and passes straight through — showing a saved copy over a refusal would be
 *   showing someone data the server just said they may not have. A 404 or 410 deletes the copy.
 * - **Per account, or not at all.** Nothing is read, written or served while nobody is known to
 *   be signed in, and a copy is only ever served to the account that saved it.
 * - **Bounded**, evicting the copy least recently opened.
 *
 * Free of browser APIs: the store, the clock and the error classification are injected, which is
 * what makes the rules above testable rather than merely intended.
 */

/** What a read asked for, in enough detail to ask again. Plain strings so it can be stored. */
export interface CachedRequest {
  readonly route: string;
  readonly params?: Readonly<Record<string, string>>;
  readonly query?: Readonly<Record<string, string>>;
}

export interface CacheEntry {
  readonly userId: string;
  readonly request: CachedRequest;
  readonly data: unknown;
  /** When the server last gave us this, ISO. What the scorer is told. */
  readonly savedAt: string;
  /** When the scorer last looked at it, ISO. What eviction and background refresh order by. */
  readonly openedAt: string;
}

export interface CacheStore {
  get(key: string): Promise<CacheEntry | undefined>;
  put(key: string, entry: CacheEntry): Promise<void>;
  delete(key: string): Promise<void>;
  all(): Promise<readonly { readonly key: string; readonly entry: CacheEntry }[]>;
}

/**
 * What a failed read means.
 *
 * - `unreachable` — no answer, or a server error: serve the saved copy if there is one.
 * - `answered`    — the server said no (401, 403, 422…): pass it on, never mask it.
 * - `gone`        — the server said it does not exist (404, 410): drop the copy, pass it on.
 */
export type Classification = "unreachable" | "answered" | "gone";

export interface ReadCacheOptions {
  readonly store: CacheStore;
  /** The signed-in account, or null when unknown. Read on every call. */
  readonly userId: () => string | null;
  readonly classify: (error: unknown) => Classification;
  readonly now?: () => Date;
  /** How many copies to keep per device. */
  readonly max?: number;
  /** Least time between background refreshes. */
  readonly refreshEveryMs?: number;
}

const DEFAULT_MAX = 100;
const DEFAULT_REFRESH_EVERY_MS = 60_000;

const stable = (record: Readonly<Record<string, string>> | undefined): string =>
  JSON.stringify(Object.entries(record ?? {}).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));

/** The same request is always the same key, whatever order its parameters arrived in. */
export const cacheKey = (userId: string, request: CachedRequest): string =>
  `cache:${userId}|${request.route}|${stable(request.params)}|${stable(request.query)}`;

export class ReadCache {
  private readonly store: CacheStore;
  private readonly userId: () => string | null;
  private readonly classify: (error: unknown) => Classification;
  private readonly now: () => Date;
  private readonly max: number;
  private readonly refreshEveryMs: number;
  private lastRefresh: number | null = null;

  constructor(options: ReadCacheOptions) {
    this.store = options.store;
    this.userId = options.userId;
    this.classify = options.classify;
    this.now = options.now ?? ((): Date => new Date());
    this.max = options.max ?? DEFAULT_MAX;
    this.refreshEveryMs = options.refreshEveryMs ?? DEFAULT_REFRESH_EVERY_MS;
  }

  /** Run `live`, remembering the answer, or fall back to the last one when there is no answer. */
  async read<T>(request: CachedRequest, live: () => Promise<T>): Promise<T> {
    const user = this.userId();
    if (user === null) return live();
    const key = cacheKey(user, request);

    try {
      const data = await live();
      await this.save(key, user, request, data, this.now().toISOString()).catch(() => undefined);
      return data;
    } catch (error: unknown) {
      const kind = this.classify(error);
      if (kind === "gone") await this.store.delete(key).catch(() => undefined);
      if (kind !== "unreachable") throw error;

      const entry = await this.store.get(key).catch(() => undefined);
      // Belt and braces: the key already names the account, but a copy is only ever handed to
      // the account that saved it, whatever a store might return.
      if (entry === undefined || entry.userId !== user) throw error;

      await this.store
        .put(key, { ...entry, openedAt: this.now().toISOString() })
        .catch(() => undefined);
      return entry.data as T;
    }
  }

  private async save(
    key: string,
    userId: string,
    request: CachedRequest,
    data: unknown,
    openedAt: string,
  ): Promise<void> {
    await this.store.put(key, { userId, request, data, savedAt: openedAt, openedAt });
    await this.evict();
  }

  private async evict(): Promise<void> {
    const all = await this.store.all();
    if (all.length <= this.max) return;
    const oldestFirst = [...all].sort((a, b) =>
      a.entry.openedAt < b.entry.openedAt ? -1 : a.entry.openedAt > b.entry.openedAt ? 1 : 0,
    );
    for (const { key } of oldestFirst.slice(0, all.length - this.max)) {
      await this.store.delete(key);
    }
  }

  /**
   * Re-read the `count` copies opened most recently, so that what is on the phone is fresh
   * before the signal drops rather than after.
   *
   * Quiet on purpose: a failure changes nothing and throws nothing, and
   * refreshing does not count as opening — otherwise a copy would keep itself alive forever by
   * being refreshed. Throttled, because the app is brought to the front many times a day.
   */
  async refreshRecent(
    count: number,
    live: (request: CachedRequest) => Promise<unknown>,
  ): Promise<void> {
    const user = this.userId();
    if (user === null) return;
    const at = this.now().getTime();
    if (this.lastRefresh !== null && at - this.lastRefresh < this.refreshEveryMs) return;
    this.lastRefresh = at;

    const mine = (await this.store.all().catch(() => []))
      .filter(({ entry }) => entry.userId === user)
      .sort((a, b) =>
        a.entry.openedAt < b.entry.openedAt ? 1 : a.entry.openedAt > b.entry.openedAt ? -1 : 0,
      )
      .slice(0, count);

    for (const { key, entry } of mine) {
      try {
        const data = await live(entry.request);
        await this.store.put(key, { ...entry, data, savedAt: this.now().toISOString() });
      } catch (error: unknown) {
        if (this.classify(error) === "gone") await this.store.delete(key).catch(() => undefined);
      }
    }
  }

  /** Forget every copy that is not this account's. For when a different account signs in. */
  async purgeExcept(userId: string): Promise<void> {
    for (const { key, entry } of await this.store.all()) {
      if (entry.userId !== userId) await this.store.delete(key);
    }
  }

  /** Forget everything. For sign-out, a lapsed session, and a guest. */
  async purgeAll(): Promise<void> {
    for (const { key } of await this.store.all()) await this.store.delete(key);
  }
}
